import styled from '@emotion/styled';
import { CardSourceListWrapper } from '@seedlang/hoc';
import { AppUI, DeckUI } from '@seedlang/state';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isPresent, pixify } from '@seedlang/utils';
import LanguageButton from 'components/button/language_button';
import SourceList from 'components/source/source_list';
import Answer from 'components/vocab_card/answer';
import { orderBy } from 'lodash';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

const Wrapper = styled.div`
  background: ${props => props.background};
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
`;

const SourceListWrapper = styled.div`
  overflow: auto;
  flex: 1;
  margin-bottom: ${props => props.marginBottom};
`;

const IconsWrapper = styled.div`
  ${flexCenterColumn()}
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  gap: 5px;
`;

const LanguageButtonWrapper = styled.div`
  width: ${props => props.width};
  height: ${props => props.height};
  background: #FFF;
  border-radius: 10px;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
`;

@observer
class CardSourceList extends React.Component {

  static defaultProps = {
    sourcePadding: '10px',
  };

  sourceLineKey = (translationSource) => {
    if (!translationSource?.source) {
      return null;
    }
    const languageId = translationSource.source.languageId || 'unknown';
    const sourceText = translationSource.source.textForUser ? translationSource.source.textForUser(AppUI.user) : translationSource.source.text;
    const genderText = translationSource.source.displayGender && !this.props.hideGender ? this.props.word?.genderAbbreviation || '' : '';
    return `${languageId}:${sourceText || ''}${genderText}`;
  }

  dedupeMeaningTranslations = (translations) => {
    const seen = new Set();
    return translations.filter(item => {
      const key = this.sourceLineKey(item?.sources?.[0]);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  isEnglishLanguageId = languageId => {
    return Boolean(languageId && languageId.startsWith('EN'));
  }

  isNativeLanguageId = languageId => {
    if (!languageId || this.isEnglishLanguageId(languageId)) {
      return false;
    }
    return languageId === AppUI.user?.languageId;
  }

  @computed get nativeLanguageIsEnglish() {
    return this.isEnglishLanguageId(AppUI.user?.languageId);
  }

  partitionMeaningSourcesByLanguage = translationSources => {
    const nativeSources = [];
    const englishSources = [];
    (translationSources || []).forEach(translationSource => {
      if (!translationSource?.source) {
        return;
      }
      if (this.isEnglishLanguageId(translationSource.source.languageId)) {
        englishSources.push(translationSource);
      } else if (this.isNativeLanguageId(translationSource.source.languageId)) {
        nativeSources.push(translationSource);
      }
    });
    return { nativeSources, englishSources };
  }

  selectMeaningSources = (translationSources, params = {}) => {
    const validSources = (translationSources || []).filter(translationSource => Boolean(translationSource?.source));
    const { nativeSources, englishSources } = this.partitionMeaningSourcesByLanguage(validSources);
    if (this.nativeLanguageIsEnglish) {
      if (params.singleMeaning) {
        return englishSources.slice(0, 2);
      }
      return validSources.slice(0, 1);
    }
    if (params.singleMeaning) {
      const selectedNativeSources = nativeSources
        .filter(translationSource => this.isNativeLanguageId(translationSource?.source?.languageId))
        .slice(0, 2);
      if (AppUI.user?.onlyShowNativeLanguage) {
        if (isPresent(selectedNativeSources)) {
          return selectedNativeSources;
        }
        return englishSources.slice(0, 1);
      }
      if (!isPresent(selectedNativeSources)) {
        return englishSources.slice(0, 1);
      }
      const englishSource = englishSources.slice(0, 1);
      if (AppUI.user?.showNativeLanguageFirst === false) {
        return englishSource.concat(selectedNativeSources);
      }
      return selectedNativeSources.concat(englishSource);
    }
    const englishSource = englishSources.slice(0, 1);
    const selectedNativeSource = nativeSources.slice(0, 1);
    if (AppUI.user?.onlyShowNativeLanguage) {
      if (isPresent(selectedNativeSource)) {
        return selectedNativeSource;
      }
      return englishSource;
    }
    if (!isPresent(selectedNativeSource)) {
      return englishSource;
    }
    if (AppUI.user?.showNativeLanguageFirst !== false) {
      return selectedNativeSource.concat(englishSource);
    }
    return englishSource.concat(selectedNativeSource);
  }

  orderMeaningTranslationsByLanguage = translations => {
    if (this.nativeLanguageIsEnglish) {
      return translations;
    }
    const nativeTranslations = [];
    const englishTranslations = [];
    (translations || []).forEach(translation => {
      if (this.isEnglishLanguageId(translation.source?.languageId)) {
        englishTranslations.push(translation);
      } else {
        nativeTranslations.push(translation);
      }
    });
    if (AppUI.user?.onlyShowNativeLanguage) {
      if (isPresent(nativeTranslations)) {
        return nativeTranslations;
      }
      return englishTranslations;
    }
    if (AppUI.user?.showNativeLanguageFirst !== false) {
      return nativeTranslations.concat(englishTranslations);
    }
    return englishTranslations.concat(nativeTranslations);
  }

  @computed get meaningTranslations() {
    const orderedMeanings = orderBy(this.props.filterMeaningsForCard(this.props.word?.meanings || []), 'score', 'desc');
    const showTwoSourcesForSingleMeaning = orderedMeanings.length === 1;

    const translations = orderedMeanings
      .flatMap(meaning => {
        const translationSources = meaning.translationSourcesForUser(AppUI.user) || [];
        const selectedSources = this.selectMeaningSources(
          translationSources,
          { singleMeaning: showTwoSourcesForSingleMeaning },
        );
        return selectedSources.map((translationSource, index) => {
          if (!translationSource?.source) return null;
          return {
            id: `${meaning.id}-${index}`,
            source: translationSource.source,
            sources: [translationSource],
          };
        });
      })
      .filter(item => item);
    return this.orderMeaningTranslationsByLanguage(this.dedupeMeaningTranslations(translations));
  }

  @computed get previewMeaningTranslations() {
    const groupedSources = {};
    (this.props.word?.meaningSourcePreviews || []).forEach((translationSource, index) => {
      if (!translationSource?.source) {
        return;
      }
      const meaningId = translationSource.meaningId || `preview-${index}`;
      if (this.props.cardSynonymMeaningIds && !this.props.cardSynonymMeaningIds.has(String(meaningId))) {
        return;
      }
      if (!groupedSources[meaningId]) {
        groupedSources[meaningId] = [];
      }
      groupedSources[meaningId].push(translationSource);
    });
    const singlePreviewMeaning = Object.keys(groupedSources).length === 1;
    const translations = Object.entries(groupedSources)
      .flatMap(([meaningId, translationSources]) => {
        const selectedSources = this.selectMeaningSources(
          translationSources,
          { singleMeaning: singlePreviewMeaning },
        );
        return selectedSources.map((translationSource, index) => {
          return {
            id: translationSource.id || `${meaningId}-${index}`,
            source: translationSource.source,
            sources: [translationSource],
          };
        });
      });
    return this.orderMeaningTranslationsByLanguage(this.dedupeMeaningTranslations(translations));
  }

  @computed get activeMeaningTranslations() {
    if (isPresent(this.meaningTranslations)) {
      return this.meaningTranslations;
    }
    return this.previewMeaningTranslations;
  }

  @computed get meaningFontSize() {
    const fontSizes = {
      1: 30,
      2: 26,
      3: 22,
      4: 20,
    };
    return fontSizes[Math.min(this.activeMeaningTranslations.length, 4)];
  }

  @computed get shouldShowMeaningTranslations() {
    return AppUI.user?.showMeanings && AppUI.user?.showVocabMeanings === true;
  }

  @computed get showMeaningTranslationFlags() {
    const languageIds = new Set(
      this.activeMeaningTranslations
        .map(translation => translation.source?.languageId)
        .filter(languageId => languageId),
    );
    return languageIds.size > 1;
  }

  openVocabOverlay = () => {
    DeckUI.openDeckVocabCard({
      card: this.props.card,
      cardState: this.props.card?.cardState,
      requiresRevealed: true,
    });
  }

  render() {
    return (
      <Wrapper
        background={this.props.background}
      >
        <IconsWrapper>
          {
            this.props.showLanguageButton &&
              <LanguageButtonWrapper
                onClick={this.props.toggleLanguage}
                height={DeckUI.layout.minimumHeight ? '30px' : '40px'}
                width={DeckUI.layout.minimumHeight ? '30px' : '40px'}
              >
                <LanguageButton
                  arrowLeft={this.props.arrowLeft}
                  width={this.props.languageIconWidth}
                  languageId={this.props.showType === 'source' ? this.props.sentence?.sourceTextForUserLanguageId(DeckUI.user) || 'EN' : DeckUI.deck?.languageId || this.props.card?.languageId || DeckUI.targetLanguageId}
                />
              </LanguageButtonWrapper>
          }
        </IconsWrapper>

        <SourceListWrapper
          marginBottom={this.props.showWrongAnswer && isPresent(this.props.wrongAnswer) ? '50px' : 0}
        >
          {
            this.shouldShowMeaningTranslations && isPresent(this.activeMeaningTranslations) &&
              <SourceList
                {...this.props}
                flagWrapperWidth='25px'
                flagWrapperMargin='0 0 0 5px'
                showFlags={this.showMeaningTranslationFlags}
                centerTextInDeck
                borderBottom='1px solid #909090'
                hideRetranslationIcon
                sourcePadding={this.props.sourcePadding}
                fontSize={pixify(this.meaningFontSize)}
                translations={this.activeMeaningTranslations}
                word={this.props.word}
                hideGender={this.props.hideGender}
                user={AppUI.user}
                onClick={this.props.card?.cardState?.revealed ? this.openVocabOverlay : null}
                numberPreviewSources={this.props.numberPreviewSources}
                preview={false}
                preserveTranslationOrder
              />
          }
          {
            !(this.shouldShowMeaningTranslations && isPresent(this.activeMeaningTranslations)) &&
              <SourceList
                {...this.props}
                flagWrapperWidth='25px'
                flagWrapperMargin='0 0 0 5px'
                showFlags={!['EN','EN2'].includes(AppUI.user.languageId)}
                centerTextInDeck
                borderBottom='1px solid #909090'
                showAddSource
                hideRetranslationIcon
                sourcePadding={this.props.sourcePadding}
                fontSize={pixify(this.props.fontSize)}
                translations={this.props.translations}
                hideGender={this.props.hideGender}
                user={AppUI.user}
                preferredSource={this.props.card.source}
                onClick={Boolean(this.props.showEdit) && this.props.card?.cardState?.revealed ? this.openVocabOverlay : null}
                numberPreviewSources={this.props.numberPreviewSources}
              />
          }
        </SourceListWrapper>
        {
          this.props.card.cardState?.revealed && this.props.showWrongAnswer && isPresent(this.props.wrongAnswer) &&
            <Answer
              text={this.props.wrongAnswer}
              width='100%'
            />
        }
      </Wrapper>
    );
  }
}

export default CardSourceListWrapper(CardSourceList);
